import React from 'react'
import { cn } from '../../design-system/utils'
import { Wifi, WifiOff, AlertTriangle, X, RefreshCw } from 'lucide-react'

/**
 * Progress Loading Overlay Component
 */
export const ProgressLoadingOverlay = ({ 
  isLoading, 
  message = 'Loading...', 
  showProgress = false,
  children,
  className,
  ...props 
}) => {
  if (!isLoading) {
    return children
  }

  return (
    <div className={cn('relative', className)} {...props}>
      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-text-secondary">{message}</p>
          {showProgress && (
            <div className="w-48 bg-gray-200 rounded-full h-2 mt-4">
              <div className="bg-primary-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}

/**
 * Offline Mode Indicator Component
 */
export const OfflineModeIndicator = ({ className, ...props }) => {
  return (
    <div 
      className={cn(
        'bg-warning-100 border border-warning-200 rounded-lg p-3 flex items-center gap-2',
        className
      )} 
      {...props}
    >
      <WifiOff className="w-4 h-4 text-warning-600" />
      <span className="text-sm text-warning-700">
        You're offline. Using cached content.
      </span>
    </div>
  )
}

/**
 * Network Error Banner Component
 */
export const NetworkErrorBanner = ({ 
  error, 
  onRetry, 
  onDismiss, 
  className,
  ...props 
}) => {
  return (
    <div 
      className={cn(
        'bg-error-100 border border-error-200 rounded-lg p-3 flex items-center justify-between',
        className
      )} 
      {...props}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-error-600" />
        <span className="text-sm text-error-700">
          {error || 'Network error occurred'}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-sm text-error-600 hover:text-error-800 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-error-600 hover:text-error-800"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Connection Status Component
 */
export const ConnectionStatus = ({ isOnline, className, ...props }) => {
  return (
    <div 
      className={cn(
        'flex items-center gap-2 px-2 py-1 rounded-full text-xs',
        isOnline 
          ? 'bg-success-100 text-success-700' 
          : 'bg-error-100 text-error-700',
        className
      )} 
      {...props}
    >
      {isOnline ? (
        <Wifi className="w-3 h-3" />
      ) : (
        <WifiOff className="w-3 h-3" />
      )}
      <span>{isOnline ? 'Online' : 'Offline'}</span>
    </div>
  )
}

export default {
  ProgressLoadingOverlay,
  OfflineModeIndicator,
  NetworkErrorBanner,
  ConnectionStatus
}